import { useState } from 'react';
import { App, Dropdown, Spin } from 'antd';
import { CheckOutlined, DownOutlined, ShopOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import {
  useGetAccessibleRestaurantsQuery,
  useSwitchRestaurantMutation,
} from '../../services/restaurantsApi';
import { setCredentials } from '../../store/slices/authSlice';

/**
 * Selector de sucursal del header. Con un solo restaurante accesible se pinta el
 * nombre plano de siempre; con dos o más, un dropdown.
 *
 * El cambio recarga la app completa a propósito: el cache de RTK Query del POS no
 * lleva el restaurante en la key, el grupo de SignalR se fija al abrir el socket
 * con el JWT viejo, y la zona horaria/moneda salen de un query cacheado. Guardar
 * los tokens nuevos y recargar deja todo eso limpio de un golpe.
 */
const RestaurantSwitcher = ({ compact }: { compact?: boolean }) => {
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const currentRestaurant = useSelector((state: RootState) => state.restaurant.currentRestaurant);
  const { data: restaurants } = useGetAccessibleRestaurantsQuery();
  const [switchRestaurant] = useSwitchRestaurantMutation();
  const [switching, setSwitching] = useState(false);

  if (!currentRestaurant) return null;

  const options = restaurants ?? [];
  if (options.length < 2) {
    return compact ? null : (
      <span className="layout-header-restname">{currentRestaurant.name}</span>
    );
  }

  const handleSwitch = async (restaurantId: string) => {
    if (restaurantId === currentRestaurant.id || switching) return;
    setSwitching(true);
    try {
      const response = await switchRestaurant({ restaurantId }).unwrap();
      dispatch(setCredentials(response));
      // A la raíz, no a la URL actual: la página abierta puede referir datos
      // (una orden, una mesa) que no existen en la otra sucursal.
      window.location.assign('/');
    } catch (error: any) {
      setSwitching(false);
      message.error(error?.data?.message || 'No se pudo cambiar de restaurante');
    }
  };

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: options.map((r) => ({
          key: r.id,
          label: r.name,
          icon:
            r.id === currentRestaurant.id ? <CheckOutlined /> : <ShopOutlined />,
        })),
        selectable: false,
        onClick: ({ key }) => handleSwitch(key),
      }}
    >
      <button
        type="button"
        className="layout-header-restname restaurant-switcher-trigger"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: 0,
        }}
      >
        {switching ? <Spin size="small" /> : null}
        <span>{compact ? currentRestaurant.name?.charAt(0).toUpperCase() : currentRestaurant.name}</span>
        <DownOutlined style={{ fontSize: 10, opacity: 0.65 }} />
      </button>
    </Dropdown>
  );
};

export default RestaurantSwitcher;
